import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import Header from '../components/Header';
import ProposePlayModal from '../components/ProposePlayModal';
import GroupAdminDashboard from '../components/GroupAdminDashboard';
import { PLAY_TYPES, PLAY_FORMATS } from '../constants';
import ClickableUsername from '../components/ClickableUsername';
import { formatTime } from '../utils/dateTimeUtils';

export default function GroupDetailsPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // State for group data
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  // New state for members list
  const [showAllMembers, setShowAllMembers] = useState(false);

  // Delete proposal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch group data
  useEffect(() => {
    // Wait for auth to be initialized
    if (authLoading) return;
    
    // Redirect if no user
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (!groupId) return;

    const fetchGroupData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', groupId)
          .single();

        if (groupError) throw groupError;
        if (!groupData) {
          setError('Group not found');
          setLoading(false);
          return;
        }

        setGroup(groupData);
        setIsAdmin(groupData.admin_id === user.id);

        // Fetch group members with their profiles
        const { data: membersData, error: membersError } = await supabase
          .from('group_members')
          .select(`
            id,
            user_id,
            role,
            profiles!inner (
              id,
              first_name,
              last_name,
              email,
              skill_level,
              avatar_url
            )
          `)
          .eq('group_id', groupId);

        if (membersError) {
          console.error('Error fetching members:', membersError);
          throw membersError;
        }

        // Check if the current user is a member
        const isMember = membersData.some(m => m.user_id === user.id);
        if (!isMember && groupData.visibility === 'private') {
          setError('You do not have access to this group');
          setLoading(false);
          return;
        }

        setMembers(membersData.map(m => ({
          id: m.user_id,
          name: `${m.profiles.first_name} ${m.profiles.last_name}`,
          skillLevel: m.profiles.skill_level,
          role: m.role,
          avatar_url: m.profiles.avatar_url
        })));

        // Fetch group proposals
        const fetchProposals = async () => {
          try {
            // Get current date and time
            const now = new Date();
            const today = now.toISOString().split('T')[0];
            const currentTime = now.toLocaleTimeString('en-US', { hour12: false });

            const { data, error } = await supabase
              .from('play_proposals')
              .select(`
                *,
                proposer:proposer_id (
                  first_name,
                  last_name,
                  skill_level
                ),
                proposal_participants (
                  user_id,
                  status
                )
              `)
              .eq('group_id', groupId)
              .eq('status', 'open')
              .or(`date.gt.${today},and(date.eq.${today},time.gt.${currentTime})`)
              .order('date', { ascending: true });

            if (error) throw error;

            // Filter out proposals that are full before setting state
            const openProposals = data.filter(p => {
              const acceptedCount = p.proposal_participants.filter(pp => pp.status === 'accepted').length;
              return acceptedCount < p.max_players;
            });

            setProposals(openProposals.map(p => ({
              id: p.id,
              type: p.type,
              format: p.format,
              date: new Date(p.date).toLocaleDateString('en-US', { 
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }),
              time: formatTime(p.time),
              location: p.location,
              proposerSkillLevel: p.proposer.skill_level,
              status: p.status,
              acceptedCount: p.proposal_participants.filter(pp => pp.status === 'accepted').length,
              requiredPlayers: p.max_players,
              proposer: `${p.proposer.first_name} ${p.proposer.last_name}`,
              proposer_id: p.proposer_id,
              comments: p.comments
            })));
          } catch (error) {
            console.error('Error fetching proposals:', error);
            setError('Failed to fetch proposals');
          }
        };

        fetchProposals();

      } catch (error) {
        console.error('Error fetching group data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupData();
  }, [groupId, user, authLoading, navigate]);

  // Form data for propose play modal
  const [proposePlayForm, setProposePlayForm] = useState({
    group: groupId,
    typeOfPlay: '',
    format: '',
    date: '',
    time: '',
    location: '',
    comments: '',
    maxPlayers: '',
    minPlayers: ''
  });

  // Handle form changes
  const handleProposePlayChange = (e) => {
    const { name, value } = e.target;
    setProposePlayForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleProposePlaySubmit = async (formData) => {
    if (!formData) return;
    let createdProposalId = null;
    
    try {
      setLoading(true);
      
      // Insert new proposal
      const { data: proposalData, error: proposalError } = await supabase
        .from('play_proposals')
        .insert([{
          group_id: groupId,
          type: formData.typeOfPlay,
          format: PLAY_FORMATS.SINGLES, // Both types now default to Singles
          date: formData.date,
          time: formData.time,
          location: formData.location,
          comments: formData.comments,
          proposer_id: user.id,
          max_players: 2, // Both Match and Just for Fun are now Singles (2 players)
          min_players: null, // Remove min_players since we're not using custom player counts
          status: 'open'
        }])
        .select()
        .single();

      if (proposalError) {
        console.error('Error creating proposal:', proposalError);
        
        // Handle rate limit errors specifically
        if (proposalError.message && proposalError.message.includes('RATE_LIMIT_EXCEEDED')) {
          throw new Error('You have reached your limit of 5 proposals per hour. Please try again later.');
        } else {
          throw proposalError;
        }
      }
      
      console.log('Proposal created successfully:', proposalData);
      createdProposalId = proposalData.id;

      // Add proposer as first participant
      const { error: participantError } = await supabase
        .from('proposal_participants')
        .insert([{
          proposal_id: proposalData.id,
          user_id: user.id,
          status: 'accepted'
        }]);

      if (participantError) {
        console.error('Error adding participant:', participantError);
        // Rollback proposal creation
        await supabase.from('play_proposals').delete().eq('id', proposalData.id);
        throw new Error('Failed to add you as participant. Please try again.');
      }

      console.log('Added proposer as participant');

      // Send notification emails to all group members
      setIsSendingEmails(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-proposal-notification-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              proposalId: proposalData.id
            })
          }
        );

        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to send notification emails');
        }

        console.log('Notification emails sent successfully');
        setSuccessMessage('🎾 Proposal created and notifications sent! Your group members have been emailed.');
      } catch (emailError) {
        console.error('Error sending notification emails:', emailError);
        
        // Rollback: Delete the proposal and participant
        await supabase.from('proposal_participants').delete().eq('proposal_id', proposalData.id);
        await supabase.from('play_proposals').delete().eq('id', proposalData.id);
        
        throw new Error('Failed to send notification emails. Proposal not created. Please try again.');
      } finally {
        setIsSendingEmails(false);
      }

      // Reset form and close modal
      setProposePlayForm({
        group: groupId,
        typeOfPlay: '',
        format: '',
        date: '',
        time: '',
        location: '',
        comments: '',
        maxPlayers: '',
        minPlayers: ''
      });
      setShowProposeModal(false);

      // Get current date and time
      const now = new Date();
      const currentTime = now.toLocaleTimeString('en-US', { hour12: false });
      const today = now.toISOString().split('T')[0];

      const { data: newProposals, error: refreshError } = await supabase
        .from('play_proposals')
        .select(`
          *,
          profiles!play_proposals_proposer_id_fkey (
            first_name,
            last_name,
            skill_level
          ),
          proposal_participants (
            user_id,
            status
          )
        `)
        .eq('group_id', groupId)
        .eq('status', 'open')
        .or(`date.gt.${today},and(date.eq.${today},time.gt.${currentTime})`)
        .order('date', { ascending: true });

      if (refreshError) throw refreshError;
      
      setProposals(newProposals.map(p => ({
        id: p.id,
        type: p.type,
        format: p.format,
        date: new Date(p.date).toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        time: formatTime(p.time),
        location: p.location,
        proposerSkillLevel: p.profiles.skill_level,
        status: p.status,
        acceptedCount: p.proposal_participants.filter(pp => pp.status === 'accepted').length,
        requiredPlayers: p.max_players,
        proposer: `${p.profiles.first_name} ${p.profiles.last_name}`,
        comments: p.comments
      })));

    } catch (error) {
      console.error('Error creating play proposal:', error);
      setError(error.message);
      setIsSendingEmails(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle accepting a proposal
  const handleAcceptProposal = async (proposalId) => {
    try {
      setLoading(true);

      // Get current date and time for filtering
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const currentTime = now.toLocaleTimeString('en-US', { hour12: false });

      // First, get the proposal details to check max_players
      const { data: proposal, error: proposalError } = await supabase
        .from('play_proposals')
        .select(`
          *,
          proposal_participants (
            user_id,
            status
          )
        `)
        .eq('id', proposalId)
        .single();

      if (proposalError) throw proposalError;

      // Accept the proposal first
      const { error: acceptError } = await supabase
        .from('proposal_participants')
        .insert([{
          proposal_id: proposalId,
          user_id: user.id,
          status: 'accepted'
        }]);

      if (acceptError) throw acceptError;

      // Check if we've reached max_players
      const currentAcceptedCount = proposal.proposal_participants.filter(p => p.status === 'accepted').length + 1; // +1 for the new acceptance
      
      if (currentAcceptedCount >= proposal.max_players) {
        // This fills the proposal - try to send email and update status
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          const response = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-proposal-accepted-email`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({
                proposalId: proposalId
              })
            }
          );

          const result = await response.json();
          
          if (!response.ok || !result.success) {
            throw new Error(result.error || 'Failed to send confirmation email');
          }

          // Email succeeded, now update proposal status to 'confirmed'
          const { error: updateError } = await supabase
            .from('play_proposals')
            .update({ status: 'confirmed' })
            .eq('id', proposalId);

          if (updateError) throw updateError;

          // Show success message
          setSuccessMessage('🎾 Your match is ready and scheduled! Check your email for details.');
          
        } catch (emailError) {
          console.error('Error calling email function:', emailError);
          
          // Email failed - remove the participant we just added
          const { error: removeError } = await supabase
            .from('proposal_participants')
            .delete()
            .eq('proposal_id', proposalId)
            .eq('user_id', user.id);

          if (removeError) {
            console.error('Error removing participant after email failure:', removeError);
          }

          setError('Something went wrong - this match can\'t be accepted. Please try again later.');
          setLoading(false);
          return; // Don't continue with UI updates if email fails
        }
      } else if (proposal.format === 'doubles') {
        // Show message for doubles match that's not yet full
        const playersNeeded = proposal.max_players - currentAcceptedCount;
        setSuccessMessage(`🎾 Thanks for joining! You'll be notified by email when ${playersNeeded} more player${playersNeeded > 1 ? 's' : ''} join and the match is finalized.`);
      }

      // Refresh proposals list
      const { data: newProposals, error: refreshError } = await supabase
        .from('play_proposals')
        .select(`
          *,
          profiles!play_proposals_proposer_id_fkey (
            first_name,
            last_name,
            skill_level
          ),
          proposal_participants (
            user_id,
            status
          )
        `)
        .eq('group_id', groupId)
        .eq('status', 'open')
        .or(`date.gt.${today},and(date.eq.${today},time.gt.${currentTime})`)
        .order('date', { ascending: true });

      if (refreshError) throw refreshError;
      
      setProposals(newProposals.map(p => ({
        id: p.id,
        type: p.type,
        format: p.format,
        date: new Date(p.date).toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        time: p.time,
        location: p.location,
        proposerSkillLevel: p.profiles.skill_level,
        status: p.status,
        acceptedCount: p.proposal_participants.filter(pp => pp.status === 'accepted').length,
        requiredPlayers: p.max_players,
        proposer: `${p.profiles.first_name} ${p.profiles.last_name}`,
        comments: p.comments
      })));

      setLoading(false);
    } catch (error) {
      console.error('Error accepting proposal:', error);
      setLoading(false);
    }
  };

  // Handle deleting a proposal
  const handleDeleteProposal = async () => {
    if (!proposalToDelete) return;
    
    try {
      setLoading(true);
      setDeleteError(null);

      // First delete all participants
      const { error: participantsError } = await supabase
        .from('proposal_participants')
        .delete()
        .eq('proposal_id', proposalToDelete.id);

      if (participantsError) {
        throw participantsError;
      }

      // Then delete the proposal
      const { error: proposalError } = await supabase
        .from('play_proposals')
        .delete()
        .eq('id', proposalToDelete.id);

      if (proposalError) {
        throw proposalError;
      }

      setSuccessMessage('Proposal deleted successfully');
      setShowDeleteConfirm(false);
      setProposalToDelete(null);
      
      // Refresh proposals list
      const fetchProposals = async () => {
        try {
          const now = new Date();
          const today = now.toISOString().split('T')[0];
          const currentTime = now.toLocaleTimeString('en-US', { hour12: false });

          const { data, error } = await supabase
            .from('play_proposals')
            .select(`
              *,
              proposer:proposer_id (
                first_name,
                last_name,
                skill_level
              ),
              proposal_participants (
                user_id,
                status
              )
            `)
            .eq('group_id', groupId)
            .eq('status', 'open')
            .or(`date.gt.${today},and(date.eq.${today},time.gt.${currentTime})`)
            .order('date', { ascending: true });

          if (error) throw error;

          const openProposals = data.filter(p => {
            const acceptedCount = p.proposal_participants.filter(pp => pp.status === 'accepted').length;
            return acceptedCount < p.max_players;
          });

          setProposals(openProposals.map(p => ({
            id: p.id,
            type: p.type,
            format: p.format,
            date: new Date(p.date).toLocaleDateString('en-US', { 
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }),
            time: formatTime(p.time),
            location: p.location,
            proposerSkillLevel: p.proposer.skill_level,
            status: p.status,
            acceptedCount: p.proposal_participants.filter(pp => pp.status === 'accepted').length,
            requiredPlayers: p.max_players,
            proposer: `${p.proposer.first_name} ${p.proposer.last_name}`,
            proposer_id: p.proposer_id,
            comments: p.comments
          })));
        } catch (error) {
          console.error('Error fetching proposals:', error);
        }
      };

      fetchProposals();
    } catch (error) {
      console.error('Error deleting proposal:', error);
      setDeleteError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle leaving group
  const handleLeaveGroup = async () => {
    try {
      setLoading(true);
      
      // Don't allow admin to leave without transferring ownership
      if (isAdmin) {
        setError("Please transfer group ownership before leaving.");
        return;
      }

      const { error: leaveError } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', user.id);

      if (leaveError) throw leaveError;

      setSuccessMessage('Successfully left the group');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error leaving group:', error);
      setError('Failed to leave group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle inviting members
  const handleInviteMember = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // First check if the email exists in profiles
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', inviteEmail)
        .single();

      if (profileError) {
        setError('User not found with this email.');
        return;
      }

      // Check if already a member
      const { data: existingMember } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', groupId)
        .eq('user_id', profileData.id)
        .single();

      if (existingMember) {
        setError('User is already a member of this group.');
        return;
      }

      // Add member to group
      const { error: inviteError } = await supabase
        .from('group_members')
        .insert([{
          group_id: groupId,
          user_id: profileData.id,
          role: 'member'
        }]);

      if (inviteError) throw inviteError;

      setSuccessMessage('Successfully invited member to the group');
      setInviteEmail('');
      setShowInviteModal(false);
      
      // Refresh members list
      const { data: newMembers, error: membersError } = await supabase
        .from('group_members')
        .select(`
          user_id,
          role,
          profiles!inner (
            id,
            first_name,
            last_name,
            skill_level,
            avatar_url
          )
        `)
        .eq('group_id', groupId);

      if (membersError) throw membersError;
      
      setMembers(newMembers.map(m => ({
        id: m.user_id,
        name: `${m.profiles.first_name} ${m.profiles.last_name}`,
        skillLevel: m.profiles.skill_level,
        role: m.role,
        avatar_url: m.profiles.avatar_url
      })));
    } catch (error) {
      console.error('Error inviting member:', error);
      setError('Failed to invite member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Sort members to put admin first, then limit to first 3 if not showing all
  const sortedMembers = [...members].sort((a, b) => 
    a.role === 'admin' ? -1 : b.role === 'admin' ? 1 : 0
  );
  const displayedMembers = showAllMembers ? sortedMembers : sortedMembers.slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8]">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/4"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F8F8]">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-[#F8F8F8]">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
              Group not found
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-textMed hover:text-textDark transition-colors"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 mr-2" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L4.414 9H17a1 1 0 110 2H4.414l5.293 5.293a1 1 0 010 1.414z" 
                clipRule="evenodd" 
              />
            </svg>
            Back
          </button>
        </div>

        {/* Group Header */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold text-textDark mb-2">{group?.name}</h1>
                <p className="text-textMed">{members.length} members</p>
              </div>
              <div className="flex space-x-2">
                {!isAdmin && (
                  <button
                    onClick={handleLeaveGroup}
                    className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition"
                  >
                    Leave Group
                  </button>
                )}
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark"
                >
                  Invite Member
                </button>
              </div>
            </div>
            
            {isAdmin && (
              <button
                onClick={() => setShowAdminDashboard(!showAdminDashboard)}
                className="mt-4 px-4 py-2 bg-primary/5 text-primary font-semibold rounded-lg hover:bg-primary/10"
              >
                {showAdminDashboard ? 'Hide Admin Dashboard' : 'Show Admin Dashboard'}
              </button>
            )}
          </div>
        </div>

        {/* Admin Dashboard */}
        {isAdmin && showAdminDashboard && (
          <div className="max-w-4xl mx-auto mb-8">
            <GroupAdminDashboard 
              group={group}
              members={members}
              onUpdateGroup={() => {
                // Refresh group data
              }}
            />
          </div>
        )}

        {/* Group Description */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-textDark mb-4">About</h2>
            <p className="text-textMed leading-relaxed">{group?.description}</p>
          </div>
        </div>

        {/* Group Members List */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-textDark">Group Members</h2>
              <span className="text-sm text-gray-500">{members.length} members</span>
            </div>
            
            {members.length === 0 ? (
              <div className="text-center py-8">
                <div className="font-bold text-2xl mb-2">
                  <div className="text-black">No</div>
                  <div className="text-[#0D9488]">Members</div>
                </div>
                <p className="text-gray-600">
                  This group has no members yet.
                </p>
              </div>
            ) : (
              <>
                <div className="divide-y">
                  {displayedMembers.map(member => (
                    <div key={member.id} className="py-3 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                          {member.avatar_url ? (
                            <img
                              src={member.avatar_url}
                              alt={`${member.name}'s avatar`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <svg
                              className="w-6 h-6 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {member.name}
                            {member.role === 'admin' && (
                              <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                Admin
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">NTRP {member.skillLevel}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {members.length > 3 && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowAllMembers(!showAllMembers)}
                      className="text-primary hover:text-primaryDark font-medium inline-flex items-center space-x-1"
                    >
                      <span>{showAllMembers ? 'Show Less' : `Show All Members (${members.length})`}</span>
                      <svg
                        className={`w-5 h-5 transform transition-transform ${showAllMembers ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Create Proposal Button */}
        <div className="max-w-4xl mx-auto mb-8">
          <button
            onClick={() => setShowProposeModal(true)}
            className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark"
          >
            Propose New Play
          </button>
        </div>

        {/* Open Proposals */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#F0F4FF] py-3 px-4 mb-6">
            <h2 className="text-xl font-bold text-textDark">Open Proposals</h2>
          </div>
          <div className="space-y-4">
            {proposals.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="text-center">
                  <div className="font-bold text-2xl">
                    <div className="text-black">No Open</div>
                    <div className="text-[#0D9488]">Proposals</div>
                  </div>
                  <p className="text-gray-600 mt-4 mb-6">Be the first to propose a play session!</p>
                  <button
                    onClick={() => setShowProposeModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    Create Proposal
                  </button>
                </div>
              </div>
            ) : (
              proposals.map(proposal => (
                <div key={proposal.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-textDark mb-1">
                        {proposal.type.charAt(0).toUpperCase() + proposal.type.slice(1)} - {proposal.format}
                      </h3>
                      <p className="text-textMed">
                        Proposed by {proposal.proposer} • NTRP {proposal.proposerSkillLevel}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-textDark">{proposal.date}</p>
                      <p className="text-textMed">{formatTime(proposal.time)}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-textMed">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {proposal.location}
                    </div>

                    {proposal.comments && (
                      <div className="text-textMed">
                        <p className="italic">"{proposal.comments}"</p>
                      </div>
                    )}

                    <div className="flex items-center text-textMed">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {proposal.acceptedCount}/{proposal.requiredPlayers} players joined
                    </div>
                  </div>

                  {proposal.acceptedCount < proposal.requiredPlayers && (
                    proposal.proposer_id === user.id ? (
                      <div className="flex space-x-2 mt-4">
                        <button
                          className="flex-1 py-2 bg-gray-400 text-white font-semibold rounded-lg cursor-not-allowed"
                          disabled
                        >
                          Your Proposal
                        </button>
                        <button
                          onClick={() => {
                            setProposalToDelete(proposal);
                            setShowDeleteConfirm(true);
                          }}
                          className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleAcceptProposal(proposal.id)}
                        className="w-full mt-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
                      >
                        Join Play
                      </button>
                    )
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <ProposePlayModal
        show={showProposeModal}
        onClose={() => setShowProposeModal(false)}
        onSubmit={handleProposePlaySubmit}
        formData={proposePlayForm}
        onChange={handleProposePlayChange}
        groupOptions={group ? [{ id: group.id, name: group.name }] : []}
        userSkillLevel={profile?.skill_level}
        isEditing={false}
        isSubmitting={isSendingEmails}
      />

      {/* Invite Modal */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${showInviteModal ? 'block' : 'hidden'}`}
        onClick={() => setShowInviteModal(false)}
      >
        <div
          className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-textDark">Invite Member</h2>
            <button
              onClick={() => setShowInviteModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleInviteMember}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Enter member's email"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark"
            >
              Send Invitation
            </button>
          </form>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-lg">
            <div className="text-center">
              <h3 className="text-xl font-bold mb-2 text-textDark">Delete Proposal?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this proposal?
                <br />
                <span className="text-sm text-gray-500">This action cannot be undone.</span>
              </p>
              {deleteError && (
                <p className="text-red-600 mb-4">{deleteError}</p>
              )}
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setProposalToDelete(null);
                    setDeleteError(null);
                  }}
                  className="px-6 py-2 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProposal}
                  className="px-6 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage('')} />
      )}
    </div>
  );
}

// Success message component
const SuccessMessage = ({ message, onClose }) => (
  <div className="fixed top-4 right-4 bg-green-50 border-l-4 border-green-500 p-4 rounded shadow-lg z-50 animate-fade-in-down">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-green-700">{message}</p>
      </div>
      <div className="ml-auto pl-3">
        <div className="-mx-1.5 -my-1.5">
          <button
            onClick={onClose}
            className="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none"
          >
            <span className="sr-only">Dismiss</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
); 